/**
 * ChamadosPage — TODOS chamados do cliente com filtros (Gestor only).
 *
 * B1.6 spec §6 + FEAT-B1.6.3 (filtros — pedido Matheus 2026-05-28 noite).
 *
 * Filtros disponíveis:
 *   - Busca por número humano (`#42`) ou texto livre no título
 *   - Data início + data fim (range no `criadoEmIso`)
 *   - Status (dropdown DINÂMICO baseado em `kanban_custom_columns`
 *     do workflow `corretiva` — admin adiciona/remove coluna, dropdown
 *     reflete automaticamente)
 *
 * Filtro é CLIENT-SIDE (lista limitada a 200 deals pelo backend — sem
 * paginação ainda). Pra produção em escala, mover pra query backend.
 *
 * NÃO REVERTER:
 *   - Filtros são UI-only (não persistem em URL/storage) — comportamento
 *     intencional pra simplicidade. Refinar se virar request.
 *   - Status dropdown buscado de `/api/v1/kanban/corretiva/columns` —
 *     fonte única de verdade pra colunas (kanban_custom_columns).
 *   - Permissão server-side via feature_key ver_todos_chamados (backend
 *     403 se sem). Frontend guard adicional via dashboard.permissoes.
 *   - Reusa ListaChamados de MeusChamadosPage (DRY).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Label,
  Select,
  SelectOption,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import {
  useChamadosTodos,
  useClienteDashboard,
  type ChamadoListItem,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

import { ListaChamados } from './MeusChamadosPage';

/** Shape de coluna retornada pelo endpoint admin (read-only consumo aqui). */
interface KanbanColumn {
  slug: string;
  label: string;
  ordem: number;
  ativo: boolean;
}

/**
 * Hook dedicado pra buscar as colunas dinâmicas do workflow corretiva.
 * Não usa cliente-api porque é endpoint admin (sem X-Client-Context) —
 * usa fetch padrão. Read-only, cacheado por 5 min.
 */
function useKanbanColumnsCorretiva() {
  return useQuery({
    queryKey: ['kanban', 'corretiva', 'columns'] as const,
    queryFn: async (): Promise<KanbanColumn[]> => {
      const r = await fetch('/api/v1/kanban/corretiva/columns', {
        credentials: 'include',
      });
      if (!r.ok) {
        // Fallback: 4 colunas hardcoded espelhando migration 145
        return [
          { slug: 'aberta', label: 'Abertas', ordem: 1, ativo: true },
          { slug: 'agendada', label: 'Agendadas', ordem: 2, ativo: true },
          { slug: 'cancelada', label: 'Canceladas', ordem: 3, ativo: true },
          { slug: 'concluida', label: 'Concluídas', ordem: 4, ativo: true },
        ];
      }
      const body = (await r.json()) as { columns?: KanbanColumn[] } | KanbanColumn[];
      const cols = Array.isArray(body) ? body : body.columns ?? [];
      return cols.filter((c) => c.ativo).sort((a, b) => a.ordem - b.ordem);
    },
    staleTime: 5 * 60_000,
  });
}

export function ChamadosPage() {
  const { clienteAtivo } = useCliente();
  const navigate = useNavigate();
  const toast = useToast();
  const dashQuery = useClienteDashboard();

  const podeVerTodos = dashQuery.data?.permissoes.ver_todos_chamados ?? false;

  // Guard client-side: se sem permissão, redirect
  useEffect(() => {
    if (dashQuery.isSuccess && !podeVerTodos) {
      toast.show('Você só tem acesso aos chamados que abriu.', {
        variant: 'info',
      });
      navigate('/cliente/dashboard', { replace: true });
    }
  }, [dashQuery.isSuccess, podeVerTodos, navigate, toast]);

  const { data, isLoading, isError, error } = useChamadosTodos(podeVerTodos);
  const colunasQuery = useKanbanColumnsCorretiva();

  // ─── Estado dos filtros ────────────────────────────────────────────
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState(''); // ISO date YYYY-MM-DD
  const [dataFim, setDataFim] = useState('');
  const [statusFiltro, setStatusFiltro] = useState(''); // '' = todos

  const itemsFiltrados = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter((c) => matchFilters(c, busca, dataInicio, dataFim, statusFiltro));
  }, [data?.items, busca, dataInicio, dataFim, statusFiltro]);

  const filtroAtivo = !!busca || !!dataInicio || !!dataFim || !!statusFiltro;

  function limparFiltros() {
    setBusca('');
    setDataInicio('');
    setDataFim('');
    setStatusFiltro('');
  }

  if (!podeVerTodos && dashQuery.isSuccess) return null;

  return (
    <PortalPage
      testId="chamados-todos-page"
      title="Todos os chamados"
      subtitle={
        clienteAtivo
          ? `Tudo que a equipe de ${clienteAtivo.nome} abriu — inclusive a portaria.`
          : 'Tudo que a equipe do condomínio abriu.'
      }
    >
      {!clienteAtivo || dashQuery.isLoading ? (
        <PortalCarregando rotulo="Carregando os chamados…" />
      ) : (
        <>
          {/* Filtros — no 375px empilham numa coluna só; as duas datas dividem
              a linha porque juntas formam um único conceito (período). */}
          <section
            className="mb-4 rounded-xl border border-line bg-paper-surface p-4 dark:border-line-dark dark:bg-paper-dark-surface"
            data-testid="chamados-filtros"
          >
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="busca">Buscar</Label>
                <Input
                  id="busca"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Número ou palavra do título"
                  data-testid="chamados-filtro-busca"
                />
              </div>
              <div>
                <Label htmlFor="data-inicio">De</Label>
                <Input
                  id="data-inicio"
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  data-testid="chamados-filtro-data-inicio"
                />
              </div>
              <div>
                <Label htmlFor="data-fim">Até</Label>
                <Input
                  id="data-fim"
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  data-testid="chamados-filtro-data-fim"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label htmlFor="status">Situação</Label>
                <Select
                  id="status"
                  value={statusFiltro}
                  onChange={(e) => setStatusFiltro(e.target.value)}
                  data-testid="chamados-filtro-status"
                >
                  <SelectOption value="">Todas</SelectOption>
                  {(colunasQuery.data ?? []).map((c) => (
                    <SelectOption key={c.slug} value={c.slug}>
                      {c.label}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>
            {filtroAtivo && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3 dark:border-line-dark">
                <span className="flex items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2">
                  <Badge variant="info" size="sm">
                    {itemsFiltrados.length} de {data?.items.length ?? 0}
                  </Badge>
                  chamados com esses filtros
                </span>
                <Button variant="ghost" size="sm" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              </div>
            )}
          </section>

          {isLoading ? (
            <PortalCarregando rotulo="Carregando os chamados…" />
          ) : isError ? (
            <Alert variant="danger">
              {mensagemErro(
                error,
                'Não conseguimos carregar os chamados agora. Tente atualizar a página.',
              )}
            </Alert>
          ) : !data || data.items.length === 0 ? (
            <EmptyState
              title="Nenhum chamado por aqui ainda"
              description="Assim que alguém do condomínio abrir um chamado, ele aparece nesta lista. Você também pode abrir o primeiro agora."
              action={
                <Link to="/cliente/chamado/novo">
                  <Button variant="primary">Abrir chamado</Button>
                </Link>
              }
            />
          ) : itemsFiltrados.length === 0 ? (
            <EmptyState
              title="Nada encontrado com esses filtros"
              description="Tente um período maior ou apague a busca."
              action={
                <Button variant="secondary" size="sm" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              }
            />
          ) : (
            <ListaChamados items={itemsFiltrados} />
          )}
        </>
      )}
    </PortalPage>
  );
}

/**
 * Match dos filtros aplicados a um chamado. Pura (testável).
 * - busca: matcha `#N` ou substring do título (case-insensitive)
 * - dataInicio/Fim: criadoEmIso >= dataInicio + 00:00 / <= dataFim + 23:59:59
 * - status: matcha fase exata (slug)
 */
function matchFilters(
  c: ChamadoListItem,
  busca: string,
  dataInicio: string,
  dataFim: string,
  status: string,
): boolean {
  if (busca.trim()) {
    const q = busca.trim().toLowerCase();
    // Match por #N humano (sem ou com #)
    if (q.startsWith('#')) {
      const numeroQuery = q.slice(1);
      if (String(c.numeroHumano ?? '') !== numeroQuery) return false;
    } else if (!Number.isNaN(Number(q)) && String(c.numeroHumano ?? '') === q) {
      // ok — match exato em número
    } else {
      // Busca substring no título
      if (!c.titulo.toLowerCase().includes(q)) return false;
    }
  }
  if (dataInicio) {
    if (c.criadoEmIso < `${dataInicio}T00:00:00.000Z`) return false;
  }
  if (dataFim) {
    if (c.criadoEmIso > `${dataFim}T23:59:59.999Z`) return false;
  }
  if (status) {
    if (c.fase !== status) return false;
  }
  return true;
}
